import {useEffect, useState} from "react";
import {css, Theme} from "@emotion/react";

import {borderRadius, calculateRemSize, flex, flexAbsoluteCenter, mh, mr, mv, ph, pv} from "@pg-design/helpers-css";
import {SearchBoldIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {form, IFormProps} from "@pg-mono/form";
import {useUserDevice} from "@pg-mono/user-device";

import {MobileOptionsModal} from "../../../atoms/MobileOptionsModal";
import {MobileSearchFilters} from "../../../search/components/MobileSearchFilters";
import {SearchAutocomplete} from "../../../search/components/search_autocomplete/SearchAutocomplete";
import {SearchAutocompleteDropdown} from "../../../search/components/search_autocomplete/SearchAutocompleteDropdown";
import {SearchAutocompleteWithDropdown} from "../../../search/components/search_autocomplete/SearchAutocompleteWithDropdown";
import {SearchFiltersWithAccordion} from "../../../search/components/SearchFiltersWithAccordion";
import {usePriceRanges} from "../../../search/hooks/use_price_ranges";
import {SearchListingInvestmentViewType, useSearchListingInvestmentViewType} from "../../../search/hooks/use_search_listing_investment_view_type";
import {useSearchParamAbroadCountry} from "../../../search/hooks/use_search_param_abroad_country";
import {ISearchFiltersFormValues} from "../../../search/types/ISearchFiltersFormValues";
import {SearchFormValuesType} from "../../../search/types/SearchFormValuesType";
import {useNotificationsFilters} from "../hooks/use_notifications_filters";
import {INotificationsSubscriptionModalProps, SelectedFormValuesType} from "./OfferListNotificationsSubscriptionModal";

interface IOwnProps extends INotificationsSubscriptionModalProps {
    onReset: () => void;
    onNotificationsFiltersSubmit: (values: SelectedFormValuesType) => void;
}

interface IProps extends IOwnProps, IFormProps<SelectedFormValuesType> {}

export const OfferListNotificationsFiltersViewC = (props: IProps) => {
    const {onReset, onNotificationsFiltersSubmit, ...legacyFormProps} = props;
    const {isMobile} = useUserDevice();
    const [showLocationSearch, setShowLocationSearch] = useState(false);
    const {searchFiltersConfig, countBySlots} = useNotificationsFilters(props.formValues);
    const searchListingInvestmentViewType = useSearchListingInvestmentViewType();
    const searchCountry = useSearchParamAbroadCountry();
    const {maxPrice} = usePriceRanges();

    const fetchAllSearchLists = (searchInput: string) => {
        const searchParams =
            searchListingInvestmentViewType === SearchListingInvestmentViewType.ABROAD && searchCountry
                ? {searchContent: {regions: true, vendors: true, offers: true, googlePOIs: false}, country: searchCountry}
                : {searchContent: {regions: true, vendors: true, offers: true, googlePOIs: true}};

        return props.fetchAllSearchLists(searchInput, searchParams);
    };

    useEffect(() => {
        if (!isMobile) {
            setShowLocationSearch(false);
        }
    }, [isMobile]);

    const chosenRegionsCount = props.formValues?.search?.regions?.length;
    const legacyFormPropsCustom = {
        ...legacyFormProps,
        onSubmit: () => onNotificationsFiltersSubmit(props.formValues),
        onAfterChange: () => null
    } as IFormProps<ISearchFiltersFormValues>;

    return (
        <>
            {isMobile && !showLocationSearch && (
                <MobileOptionsModal modalHeading="Kryteria wyszukiwania" isOpen onModalClose={onReset}>
                    <MobileSearchFilters
                        searchFiltersSlots={searchFiltersConfig.slots}
                        searchFiltersCountBySlots={countBySlots}
                        legacyFormProps={legacyFormPropsCustom}
                        onMobileCloseModal={onReset}
                        onMobileResetForm={onReset}
                        topContent={
                            <div css={locationButton} onClick={() => setShowLocationSearch(true)}>
                                <Text variant="headline_6" css={mr(1)}>
                                    Lokalizacja
                                </Text>
                                {!!chosenRegionsCount && (
                                    <Text variant="info_txt_1" css={locationCountBadge}>
                                        {chosenRegionsCount}
                                    </Text>
                                )}
                                <SearchBoldIcon size="2.4" wrapperSize="2.4" />
                            </div>
                        }
                        filtersConfig={{
                            budget: {
                                maxPrice
                            }
                        }}
                    />
                </MobileOptionsModal>
            )}
            {isMobile && showLocationSearch && (
                <MobileOptionsModal modalHeading="Szukaj" isOpen onModalClose={() => setShowLocationSearch(false)}>
                    <SearchAutocompleteWithDropdown
                        onDropdownStatusChange={() => null}
                        formProps={{
                            ...props,
                            formValues: props.formValues as SearchFormValuesType,
                            fetchAllSearchLists
                        }}
                        onSearchSubmit={() => null}
                        onAfterTabSwitch={() => null}
                        onInputMount={() => null}
                        isInModal
                        includeDistanceFilter
                    />
                </MobileOptionsModal>
            )}
            {!isMobile && (
                <>
                    <div css={searchInputWrapper}>
                        <SearchAutocomplete
                            onChange={props.formActions.update}
                            onDropdownStatusChange={() => null}
                            onAfterTabSwitch={() => null}
                            values={props.formValues}
                            fetchAllSearchLists={fetchAllSearchLists}
                            includeDistanceFilter
                            renderDropdownList={(props) => (
                                <SearchAutocompleteDropdown
                                    dropdownIsOpen={props.dropdownIsOpen}
                                    search={props.search}
                                    selectedTab={props.selectedTab}
                                    onLinkClick={props.onLinkClick}
                                    requestStateObj={props.requestStateObj}
                                    activeItem={props.activeItem}
                                    offers={props.offers}
                                    places={props.places}
                                    regions={props.regions}
                                    removeRegionTag={props.removeRegionTag}
                                    vendors={props.vendors}
                                    getFieldProps={props.getFieldProps}
                                />
                            )}
                        />
                    </div>

                    <SearchFiltersWithAccordion
                        searchFiltersSlots={searchFiltersConfig.slots}
                        searchFiltersCountBySlots={countBySlots}
                        legacyFormProps={legacyFormPropsCustom}
                        footerClearButtonText="Anuluj"
                        footerSubmitButtonText="Zatwierdź"
                        onResetForm={onReset}
                        isNotificationsSubscriptionModal
                        filtersConfig={{
                            budget: {
                                maxPrice
                            }
                        }}
                    />
                </>
            )}
        </>
    );
};

export const OfferListNotificationsFiltersView = form<SelectedFormValuesType, IProps>(OfferListNotificationsFiltersViewC);

const locationButton = (theme: Theme) => css`
    display: flex;
    align-items: center;
    ${mh(2)};
    ${mv(1)};
    border: 1px solid ${theme.colors.gray[500]};
    ${borderRadius(2)};
    ${ph(2)};
    ${pv(2)};

    svg {
        margin-left: auto;
    }
`;

const locationCountBadge = (theme: Theme) => css`
    ${flexAbsoluteCenter};
    background-color: ${theme.colors.primary};
    width: ${calculateRemSize(3)};
    height: ${calculateRemSize(3)};
    border-radius: 50%;
`;

const searchInputWrapper = css`
    ${flex()};
    width: 100%;
    flex: 1 1;
`;
import {useEffect, useState} from "react";
import {css, Theme} from "@emotion/react";

import {borderRadius, calculateRemSize, flex, flexAbsoluteCenter, mh, mr, mv, ph, pv} from "@pg-design/helpers-css";
import {SearchBoldIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {form, IFormProps} from "@pg-mono/form";
import {useUserDevice} from "@pg-mono/user-device";

import {MobileOptionsModal} from "../../../atoms/MobileOptionsModal";
import {MobileSearchFilters} from "../../../search/components/MobileSearchFilters";
import {SearchAutocomplete} from "../../../search/components/search_autocomplete/SearchAutocomplete";
import {SearchAutocompleteDropdown} from "../../../search/components/search_autocomplete/SearchAutocompleteDropdown";
import {SearchAutocompleteWithDropdown} from "../../../search/components/search_autocomplete/SearchAutocompleteWithDropdown";
import {SearchFiltersWithAccordion} from "../../../search/components/SearchFiltersWithAccordion";
import {usePriceRanges} from "../../../search/hooks/use_price_ranges";
import {SearchListingInvestmentViewType, useSearchListingInvestmentViewType} from "../../../search/hooks/use_search_listing_investment_view_type";
import {useSearchParamAbroadCountry} from "../../../search/hooks/use_search_param_abroad_country";
import {ISearchFiltersFormValues} from "../../../search/types/ISearchFiltersFormValues";
import {SearchFormValuesType} from "../../../search/types/SearchFormValuesType";
import {useNotificationsFilters} from "../hooks/use_notifications_filters";
import {INotificationsSubscriptionModalProps, SelectedFormValuesType} from "./OfferListNotificationsSubscriptionModal";

interface IOwnProps extends INotificationsSubscriptionModalProps {
    onReset: () => void;
    onNotificationsFiltersSubmit: (values: SelectedFormValuesType) => void;
}

interface IProps extends IOwnProps, IFormProps<SelectedFormValuesType> {}

export const OfferListNotificationsFiltersViewC = (props: IProps) => {
    const {onReset, onNotificationsFiltersSubmit, ...legacyFormProps} = props;
    const {isMobile} = useUserDevice();
    const [showLocationSearch, setShowLocationSearch] = useState(false);
    const {searchFiltersConfig, countBySlots} = useNotificationsFilters(props.formValues);
    const searchListingInvestmentViewType = useSearchListingInvestmentViewType();
    const searchCountry = useSearchParamAbroadCountry();
    const {maxPrice} = usePriceRanges();

    const fetchAllSearchLists = (searchInput: string) => {
        const searchParams =
            searchListingInvestmentViewType === SearchListingInvestmentViewType.ABROAD && searchCountry
                ? {searchContent: {regions: true, vendors: true, offers: true, googlePOIs: false}, country: searchCountry}
                : {searchContent: {regions: true, vendors: true, offers: true, googlePOIs: true}};

        return props.fetchAllSearchLists(searchInput, searchParams);
    };

    useEffect(() => {
        if (!isMobile) {
            setShowLocationSearch(false);
        }
    }, [isMobile]);

    const chosenRegionsCount = props.formValues?.search?.regions?.length;
    const legacyFormPropsCustom = {
        ...legacyFormProps,
        onSubmit: () => onNotificationsFiltersSubmit(props.formValues),
        onAfterChange: () => null
    } as IFormProps<ISearchFiltersFormValues>;

    return (
        <>
            {isMobile && !showLocationSearch && (
                <MobileOptionsModal modalHeading="Kryteria wyszukiwania" isOpen onModalClose={onReset}>
                    <MobileSearchFilters
                        searchFiltersSlots={searchFiltersConfig.slots}
                        searchFiltersCountBySlots={countBySlots}
                        legacyFormProps={legacyFormPropsCustom}
                        onMobileCloseModal={onReset}
                        onMobileResetForm={onReset}
                        topContent={
                            <div css={locationButton} onClick={() => setShowLocationSearch(true)}>
                                <Text variant="headline_6" css={mr(1)}>
                                    Lokalizacja
                                </Text>
                                {!!chosenRegionsCount && (
                                    <Text variant="info_txt_1" css={locationCountBadge}>
                                        {chosenRegionsCount}
                                    </Text>
                                )}
                                <SearchBoldIcon size="2.4" wrapperSize="2.4" />
                            </div>
                        }
                        filtersConfig={{
                            budget: {
                                maxPrice
                            }
                        }}
                    />
                </MobileOptionsModal>
            )}
            {isMobile && showLocationSearch && (
                <MobileOptionsModal modalHeading="Szukaj" isOpen onModalClose={() => setShowLocationSearch(false)}>
                    <SearchAutocompleteWithDropdown
                        onDropdownStatusChange={() => null}
                        formProps={{
                            ...props,
                            formValues: props.formValues as SearchFormValuesType,
                            fetchAllSearchLists
                        }}
                        onSearchSubmit={() => null}
                        onAfterTabSwitch={() => null}
                        onInputMount={() => null}
                        isInModal
                        includeDistanceFilter
                    />
                </MobileOptionsModal>
            )}
            {!isMobile && (
                <>
                    <div css={searchInputWrapper}>
                        <SearchAutocomplete
                            onChange={props.formActions.update}
                            onDropdownStatusChange={() => null}
                            onAfterTabSwitch={() => null}
                            values={props.formValues}
                            fetchAllSearchLists={fetchAllSearchLists}
                            includeDistanceFilter
                            renderDropdownList={(props) => (
                                <SearchAutocompleteDropdown
                                    dropdownIsOpen={props.dropdownIsOpen}
                                    search={props.search}
                                    selectedTab={props.selectedTab}
                                    onLinkClick={props.onLinkClick}
                                    requestStateObj={props.requestStateObj}
                                    activeItem={props.activeItem}
                                    offers={props.offers}
                                    places={props.places}
                                    regions={props.regions}
                                    removeRegionTag={props.removeRegionTag}
                                    vendors={props.vendors}
                                    getFieldProps={props.getFieldProps}
                                />
                            )}
                        />
                    </div>

                    <SearchFiltersWithAccordion
                        searchFiltersSlots={searchFiltersConfig.slots}
                        searchFiltersCountBySlots={countBySlots}
                        legacyFormProps={legacyFormPropsCustom}
                        footerClearButtonText="Anuluj"
                        footerSubmitButtonText="Zatwierdź"
                        onResetForm={onReset}
                        isNotificationsSubscriptionModal
                        filtersConfig={{
                            budget: {
                                maxPrice
                            }
                        }}
                    />
                </>
            )}
        </>
    );
};

export const OfferListNotificationsFiltersView = form<SelectedFormValuesType, IProps>(OfferListNotificationsFiltersViewC);

const locationButton = (theme: Theme) => css`
    display: flex;
    align-items: center;
    ${mh(2)};
    ${mv(1)};
    border: 1px solid ${theme.colors.gray[500]};
    ${borderRadius(2)};
    ${ph(2)};
    ${pv(2)};

    svg {
        margin-left: auto;
    }
`;

const locationCountBadge = (theme: Theme) => css`
    ${flexAbsoluteCenter};
    background-color: ${theme.colors.primary};
    width: ${calculateRemSize(3)};
    height: ${calculateRemSize(3)};
    border-radius: 50%;
`;

const searchInputWrapper = css`
    ${flex()};
    width: 100%;
    flex: 1 1;
`;
